import React from "react";

import { Command } from "./command";
import { Node, createElement, fragment } from "./tree";
import { Block, EntityDecorators, EntityMap } from "./types";

/**
 * Resolves an entity decorator config to element type and props
 */
function resolveEntityOptions(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entityDecorators: EntityDecorators<any, string>,
    entityType: string,
): {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    element: React.ComponentType<any>;
    props: Record<string, unknown>;
} | null {
    const config = entityDecorators[entityType];

    // EntityDecorators values are always ComponentType according to the type definition
    return { element: config, props: {} };
}

/**
 * EntityState manages entity rendering.
 *
 * Key insight: Entities wrap styles, not vice versa.
 * When an entity is active, styled nodes are buffered in elementStack.
 * When the entity completes, all buffered nodes are wrapped.
 */
export class EntityState {
    private entityStack: string[] = [];
    private elementStack: Node[] = [];
    private completedEntity: string | null = null;

    constructor(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        private entityDecorators: EntityDecorators<any, string>,
        private entityMap: EntityMap<string>,
    ) {}

    /**
     * Apply a command to update entity state
     */
    apply(command: Command): void {
        switch (command.name) {
            case "start_entity":
                this.entityStack.push(command.data);
                break;
            case "stop_entity": {
                const expected = this.entityStack[this.entityStack.length - 1];
                if (command.data !== expected) {
                    throw new Error(
                        `Entity mismatch: expected ${expected}, got ${command.data}`,
                    );
                }
                this.completedEntity = this.entityStack.pop()!;
                break;
            }
        }
    }

    /**
     * Check if we're currently inside an entity
     */
    hasEntity(): boolean {
        return this.entityStack.length > 0;
    }

    /**
     * Check if we're not inside any entity
     */
    hasNoEntity(): boolean {
        return this.entityStack.length === 0;
    }

    /**
     * Get entity details from the entity map
     */
    private getEntityDetails(entityKey: string) {
        const details = this.entityMap[entityKey];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!details) {
            throw new Error(
                `Entity "${entityKey}" does not exist in entityMap`,
            );
        }
        return details;
    }

    /**
     * Render entities, potentially buffering nodes or wrapping completed entities.
     *
     * Returns:
     * - The wrapped entity node if an entity just completed
     * - null if we're inside an entity (node was buffered)
     * - The input node if no entity is active
     */
    renderEntities(
        styledNode: Node,
        block: Block<string, string>,
        blocks: Block<string, string>[],
    ): Node | null {
        // We have a complete entity to render
        if (this.completedEntity !== null) {
            const entityKey = this.completedEntity;
            const entityDetails = this.getEntityDetails(entityKey);
            const options = resolveEntityOptions(
                this.entityDecorators,
                entityDetails.type,
            );

            // Build children from buffered elements
            let children: Node;
            if (this.elementStack.length === 1) {
                children = this.elementStack[0];
            } else if (this.elementStack.length > 1) {
                children = fragment(...this.elementStack);
            } else {
                // No buffered elements - use empty fragment
                children = fragment();
            }

            this.completedEntity = null;
            this.elementStack = [];

            // If there's still another entity active (adjacent entities), buffer the styled node
            if (this.hasEntity()) {
                this.elementStack.push(styledNode);
            }

            // If no decorator configured for this entity type, just return children
            if (!options) {
                return children;
            }

            // Build props from entity data
            // Pass data both as spread props (for LinkComponent) and as nested data prop (for ImageComponent)
            const props: Record<string, unknown> = {
                ...entityDetails.data,
                data: entityDetails.data,
                entity: {
                    type: entityDetails.type,
                    mutability: entityDetails.mutability,
                    block,
                    blocks,
                    entityRange: { key: entityKey },
                },
            };

            return createElement(options.element, props, children);
        }

        // Entity is active but not complete - buffer the styled node
        if (this.hasEntity()) {
            this.elementStack.push(styledNode);
            return null;
        }

        // No entity active - return node as-is
        return styledNode;
    }
}
